import { jsx as _jsx } from "react/jsx-runtime";
import { createApiClient } from '@fetmode/api-client';
import { devRequestHeaders } from '../dev/dev-settings.js';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
/** Legacy key — cleared on boot so access tokens are not left in localStorage. */
const STORAGE_KEY = 'fetmode.auth.session';
const AuthContext = createContext(null);
function clearLegacyStoredSession() {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        localStorage.removeItem(STORAGE_KEY);
    }
    catch {
        // ignore
    }
}
export function AuthProvider({ children, apiBaseUrl = '/api' }) {
    // Access token lives in memory only (XSS cannot read it from disk). Session
    // recovery uses the httpOnly refresh cookie via /v1/auth/refresh.
    const [session, setSession] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const sessionRef = useRef(session);
    sessionRef.current = session;
    const applySession = useCallback((next) => {
        const stored = {
            accessToken: next.accessToken,
            expiresIn: next.expiresIn,
            user: next.user,
        };
        setSession(stored);
    }, []);
    const setUser = useCallback((user) => {
        setSession((prev) => (prev ? { ...prev, user } : prev));
    }, []);
    const logout = useCallback(() => {
        setSession(null);
        clearLegacyStoredSession();
        void fetch(`${apiBaseUrl}/v1/auth/logout`, {
            method: 'POST',
            credentials: 'include',
        }).catch(() => undefined);
    }, [apiBaseUrl]);
    const refreshAccessToken = useCallback(async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/v1/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            if (response.status === 204 || !response.ok) {
                return null;
            }
            const refreshed = (await response.json());
            applySession(refreshed);
            return refreshed.accessToken;
        }
        catch {
            return null;
        }
    }, [apiBaseUrl, applySession]);
    const api = useMemo(() => createApiClient({
        baseUrl: apiBaseUrl,
        credentials: 'include',
        getAccessToken: () => sessionRef.current?.accessToken ?? null,
        getHeaders: devRequestHeaders,
        refreshAccessToken,
    }), [apiBaseUrl, refreshAccessToken]);
    const login = useCallback(async (input) => {
        const result = await api.login(input);
        applySession(result);
    }, [api, applySession]);
    const loginWithOAuth = useCallback(async (body) => {
        const result = await api.oauthLogin(body);
        applySession(result);
    }, [api, applySession]);
    const register = useCallback(async (input) => {
        const result = await api.register(input);
        applySession(result);
    }, [api, applySession]);
    useEffect(() => {
        clearLegacyStoredSession();
        let cancelled = false;
        void (async () => {
            try {
                const recoveredToken = await refreshAccessToken();
                if (cancelled) {
                    return;
                }
                if (!recoveredToken) {
                    setIsLoading(false);
                    return;
                }
                const user = await api.getMe();
                if (cancelled) {
                    return;
                }
                setSession((prev) => (prev ? { ...prev, user } : prev));
            }
            catch {
                if (cancelled) {
                    return;
                }
                const recovered = await refreshAccessToken();
                if (!recovered && !cancelled) {
                    logout();
                }
            }
            finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [api, logout, refreshAccessToken]);
    useEffect(() => {
        if (!session?.accessToken || !session.expiresIn) {
            return;
        }
        const refreshLeadMs = 60_000;
        const delayMs = Math.max(session.expiresIn * 1000 - refreshLeadMs, refreshLeadMs);
        const timer = setTimeout(() => {
            void refreshAccessToken().then((token) => {
                if (!token) {
                    logout();
                }
            });
        }, delayMs);
        return () => {
            clearTimeout(timer);
        };
    }, [logout, refreshAccessToken, session?.accessToken, session?.expiresIn]);
    const getAccessToken = useCallback(() => sessionRef.current?.accessToken ?? null, []);
    const value = useMemo(() => ({
        user: session?.user ?? null,
        isLoading,
        api,
        getAccessToken,
        login,
        loginWithOAuth,
        register,
        applySession,
        setUser,
        logout,
    }), [
        session?.user,
        isLoading,
        api,
        getAccessToken,
        login,
        loginWithOAuth,
        register,
        applySession,
        setUser,
        logout,
    ]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
//# sourceMappingURL=AuthProvider.js.map